import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { QueryProvider, extend } from '@thd-nucleus/data-sources';
import { IntentResolver } from './IntentResolver';
import {ExperienceContext, useStore} from "@thd-nucleus/experience-context";
import { useThdCustomer } from "@thd-olt-functional/customer-information";
import { IntentResolutionProgress } from "./IntentResolutionProgress";

const IntentResolverHoisted = ({ children }) => {
  return children;
};
IntentResolverHoisted.displayName = "IntentResolverHoisted";

IntentResolverHoisted.dataModel = extend(IntentResolver);

const isEqual = (prevProps, nextProps) => {
  return (
    prevProps.intents.join(',') === nextProps.intents.join(',') &&
    prevProps.showLoader === nextProps.showLoader &&
    prevProps.batchSize === nextProps.batchSize &&
    prevProps.showLoaderOnComplete === nextProps.showLoaderOnComplete &&
    prevProps.itemCount === nextProps.itemCount
  );
};

export const QuickIntentResolve = React.memo(({
  intents,
  itemCount,
  onAllResolved,
  onEachResolved,
  showLoader,
  batchSize = 10,
  updateProgress,
  showLoaderOnComplete = false
}) => {
  const { isLocalized } = useStore();
  const { deliveryZip: serverDeliveryZip } = useContext(ExperienceContext);
  const THDCustomer = useThdCustomer();
  const [wait, setWait] = useState(true);
  const [intentItemIdMap, setIntentItemIdMap] = useState({});
  const { svocID = '' } = THDCustomer || {};

  const intentsToResolve = useMemo(() => {
    return (intents || []).filter((intent) => intent && intent?.trim()?.length >= 1);
  }, [intents]);

  const additionalSearchParams = useMemo(() => {
    const additionalSearchData = {
      deliveryZip: serverDeliveryZip,
      svocID,
      sponsored: false
    };
    if (!isLocalized) {
      return additionalSearchData;
    }
    if (typeof window !== 'undefined' && window?.THD_LOCALIZER_AUTO_INIT?.Localizer?.getDeliveryZipcodeDetails) {
      const deliveryZipCode = window?.THD_LOCALIZER_AUTO_INIT?.Localizer?.getDeliveryZipcodeDetails();
      additionalSearchData.deliveryZip = deliveryZipCode?.zipcode;
    }
    return additionalSearchData;
  }, [serverDeliveryZip, isLocalized, svocID]);

  useEffect(() => {
    if (additionalSearchParams.deliveryZip && wait) {
      setWait(false);
    } else if (!additionalSearchParams.deliveryZip && wait) {
      setTimeout(() => setWait(false), 2000);
    }
  }, [additionalSearchParams]);

  const progress = useMemo(() => {
    const intentsCount = intentsToResolve.length;
    const completedCount = Object.keys(intentItemIdMap).length;
    const percentage = intentsCount > 0 ? Math.round((completedCount / intentsCount) * 100): 100;
    return {
      intentsCount,
      itemCount: itemCount ? itemCount : intentsCount,
      completedCount,
      resolvedCount: Object.values(intentItemIdMap).filter(items => items?.length > 0).length,
      isResolving: completedCount < intentsCount,
      resolutionProgress: percentage
    };
  }, [intentItemIdMap, intentsToResolve]);

  useEffect(() => {
    updateProgress(progress);
    if (progress.resolutionProgress === 100 && progress.intentsCount > 0) {
      onAllResolved(intentItemIdMap);
    }
  }, [progress, updateProgress, onAllResolved, intentItemIdMap]);

  const activeIntents = useMemo(() => {
    const completedCount = Object.keys(intentItemIdMap).length;
    const endIndex = Math.min(completedCount + batchSize, intentsToResolve.length);
    return intentsToResolve.slice(0, endIndex);
  }, [intentsToResolve, intentItemIdMap, batchSize]);

  const onResolved = useCallback((intent, product) => {
    const { itemIds = null } = product || {};
    // Prevent duplicate resolutions
    if (Object.hasOwn(intentItemIdMap, intent)) {
      return;
    }
    setIntentItemIdMap(prevMap => {
      return {
        ...prevMap,
        [intent]: itemIds
      };
    });
    onEachResolved(intent, itemIds);
  }, [intentsToResolve, onAllResolved, onEachResolved]);

  const showProgressBar = showLoader && (progress.isResolving || wait || showLoaderOnComplete);

  return (
    <QueryProvider cacheKey="quick-intent-resolve">
      <IntentResolverHoisted>
        {
          (activeIntents || []).map((intentItem, _index) => {
            // Skip already resolved intents
            if (Object.hasOwn(intentItemIdMap, intentItem) || wait) {
              return null;
            }
            return (
              <IntentResolver
                key={`quick-intent-resolve-${intentItem}`.replace(/\s+/g, '-')}
                intent={intentItem}
                onResolved={onResolved}
                additionalSearchParams={additionalSearchParams}
              />
            );
          })
        }
      </IntentResolverHoisted>
      {
        showProgressBar && (
          <IntentResolutionProgress progress={progress} />
        )
      }
    </QueryProvider>
  );
}, isEqual);

QuickIntentResolve.displayName = 'QuickIntentResolve';

QuickIntentResolve.propTypes = {
  intents: PropTypes.arrayOf(PropTypes.string).isRequired,
  updateProgress: PropTypes.func,
  onAllResolved: PropTypes.func,
  onEachResolved: PropTypes.func,
  showLoader: PropTypes.bool,
  showLoaderOnComplete: PropTypes.bool,
  batchSize: PropTypes.number,
  itemCount: PropTypes.number,
};

QuickIntentResolve.defaultProps = {
  onEachResolved: () => {},
  onAllResolved: () => {},
  updateProgress: () => {},
  showLoader: false,
  showLoaderOnComplete: false,
  batchSize: 10,
  itemCount: 0
};
